<template>
    <div id="modal-orders-sales-orders-apply-credit">
        <b-modal :id="modalName" title="Apply Credits" scrollable hide-footer size="xl" @shown="loadAvailableCredits">
            <template v-slot:modal-title>
                Apply Credits
            </template>

            <div v-if="loading" class="text-center">
                <b-spinner></b-spinner>
                <p>Loading available credits...</p>
            </div>

            <div v-else>
                <!-- Credits Table -->
                <b-table-simple hover small responsive bordered>
                    <b-thead>
                        <b-tr>
                            <b-th>ID</b-th>
                            <b-th>Type</b-th>
                            <b-th>Date</b-th>
                            <b-th>Reference</b-th>
                            <b-th>Notes</b-th>
                            <b-th class="text-right">Amount Total</b-th>
                            <b-th class="text-right">Amount Available</b-th>
                            <b-th>Applied To</b-th>
                            <b-th>Created By</b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="credit in availableCredits" :key="credit.id">
                            <b-td>{{ credit.id }}</b-td>
                            <b-td>
                                <b-badge :variant="credit.type === 'Credit' ? 'primary' : 'warning'">
                                    {{ credit.type }}
                                </b-badge>
                            </b-td>
                            <b-td>{{ credit.date }}</b-td>
                            <b-td>{{ credit.reference || '-' }}</b-td>
                            <b-td>{{ credit.notes || '-' }}</b-td>
                            <b-td class="text-right">${{ numberWithCommas(credit.amount_total) }}</b-td>
                            <b-td class="text-right">${{ numberWithCommas(credit.amount_available) }}</b-td>
                            <b-td>
                                <div v-if="credit.credits_applieds && credit.credits_applieds.length > 0">
                                    <div v-for="applied in credit.credits_applieds" :key="applied.id">
                                        {{ applied.sales_order.sales_order_number }} - ${{ applied.applied_amount }}
                                    </div>
                                </div>
                                <span v-else>-</span>
                            </b-td>
                            <b-td>{{ credit.created_by_user ? credit.created_by_user.name : '-' }}</b-td>
                        </b-tr>
                        <b-tr v-if="availableCredits.length === 0">
                            <b-td colspan="9" class="text-center">No available credits found</b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>

                <!-- Summary and Action Section -->
                <div class="mt-3">
                    <!-- Total Available Credit Row -->
                    <b-row class="mb-2 credit-row">
                        <b-col cols="3">
                            <strong>Total Available Credit: ${{ numberWithCommas(totalAvailableCredit) }}</strong>
                        </b-col>
                        <b-col cols="2">
                            <b-form-input 
                                v-model="creditAmount" 
                                type="text" 
                                placeholder="Amount to Apply" 
                                size="sm"
                                autocomplete="off" 
                                ref="setcreditinput" 
                                @focus.native="$event.target.select()"
                                @input="validateCreditAmount">
                            </b-form-input>
                        </b-col>
                        <b-col cols="2">
                            <b-button 
                                type="button" 
                                variant="primary" 
                                class="btn-sm"
                                @click="applyCredit"
                                :disabled="!isValidCreditAmount || submitting">
                                <span v-if="submitting">
                                    <b-spinner small type="grow"></b-spinner>
                                    Applying...
                                </span>
                                <span v-else>Apply</span>
                            </b-button>
                        </b-col>
                        <b-col cols="2">
                            <b-button 
                                type="button" 
                                variant="secondary" 
                                class="btn-sm"
                                @click="closeModal">
                                Close
                            </b-button>
                        </b-col>
                    </b-row>

                    <!-- Total Available Overpayment Row -->
                    <b-row class="mb-2 overpayment-row">
                        <b-col cols="3">
                            <strong>Total Available Overpayment: ${{ numberWithCommas(totalAvailableOverpayment) }}</strong>
                        </b-col>
                        <b-col cols="2">
                            <b-form-input 
                                v-model="overpaymentAmount" 
                                type="text" 
                                placeholder="Admin Only" 
                                size="sm"
                                disabled
                                class="admin-only-input">
                            </b-form-input>
                        </b-col>
                        <b-col cols="7">
                            <small class="admin-only-text">ADMIN ONLY</small>
                        </b-col>
                    </b-row>

                    <!-- Validation Message -->
                    <b-row v-if="creditAmount > 0">
                        <b-col cols="12" class="text-right">
                            <span v-if="creditAmount > totalAvailableCredit" class="text-danger">
                                <strong>Amount exceeds available credits!</strong>
                            </span>
                            <span v-else-if="usableBalanceDue > 0 && creditAmount > usableBalanceDue" class="text-warning">
                                <strong>Amount exceeds usable balance!</strong>
                            </span>
                            <span v-else-if="orderTotal === 0 && !hasRowQuantities" class="text-warning">
                                <strong>No balance due and no row quantities available for credit application</strong>
                            </span>
                            <span v-else class="text-success">
                                <strong>Valid amount</strong>
                            </span>
                        </b-col>
                    </b-row>

                    <!-- Balance Due -->
                    <b-row>
                        <b-col cols="12" class="text-right">
                            <strong v-if="orderTotal > 0">Balance Due: ${{ numberWithCommas(orderTotal) }}</strong>
                            <strong v-else-if="orderTotal === 0 && hasRowQuantities">
                                Usable Balance (from row quantities): ${{ numberWithCommas(usableBalanceDue) }}
                            </strong>
                            <strong v-else>Balance Due: ${{ numberWithCommas(orderTotal) }}</strong>
                            <span v-if="orderTotal === 0 && hasRowQuantities" class="text-info ml-2">
                                <small>(Calculated from row quantities)</small>
                            </span>
                        </b-col>
                    </b-row>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import commonMixin from '../../../../../mixins/common'

export default {
    mixins: [commonMixin],
    
    props: [
        'available',
        'customerId',
        'orderTotal',
        'requireConfirmation',
        'rowQuantities'
    ],

    components: {

    },

    data() {
        return {
            modalName: 'modal-orders-sales-orders-apply-credit',
            creditAmount: 0,
            overpaymentAmount: 0,
            submitting: false,
            loading: true,
            availableCredits: [],
            totalAvailable: 0,
            totalAvailableCredit: 0,
            totalAvailableOverpayment: 0
        }
    },

    methods: {
        loadAvailableCredits() {
            this.loading = true;
            let url = `api/orders/creditmemos/getavailablecredits/${this.customerId}`;
            
            axios.get(url)
                .then((res) => {
                    this.availableCredits = res.data.credits;
                    this.totalAvailable = res.data.totalAvailable;
                    
                    // Calculate separate totals for credits and overpayments
                    this.totalAvailableCredit = this.availableCredits
                        .filter(credit => credit.type === 'Credit')
                        .reduce((sum, credit) => sum + parseFloat(credit.amount_available), 0);
                    
                    this.totalAvailableOverpayment = this.availableCredits
                        .filter(credit => credit.type === 'Overpayment')
                        .reduce((sum, credit) => sum + parseFloat(credit.amount_available), 0);
                    
                    this.loading = false;
                    
                           // Set initial credit amount to the minimum of available credits and usable balance
                           this.creditAmount = Math.min(this.totalAvailableCredit, this.usableBalanceDue);
                })
                .catch(() => {
                    this.has_error = true;
                    this.loading = false;
                });
        },

        validateCreditAmount() {
            // Remove any non-numeric characters except decimal point
            this.creditAmount = this.creditAmount.toString().replace(/[^0-9.]/g, '');
            
            // Ensure only one decimal point
            const parts = this.creditAmount.split('.');
            if (parts.length > 2) {
                this.creditAmount = parts[0] + '.' + parts.slice(1).join('');
            }
            
            // Limit to 2 decimal places
            if (parts.length === 2 && parts[1].length > 2) {
                this.creditAmount = parts[0] + '.' + parts[1].substring(0, 2);
            }
        },

        applyCredit() {
            if (!this.isValidCreditAmount) {
                return;
            }

            this.submitting = true;
            this.$emit('setCredit', parseFloat(this.creditAmount));
            this.closeModal(this.modalName);
        },

        closeModal() {
            if (this.creditAmount > 0 && this.requireConfirmation) {
                this.$bvModal.msgBoxConfirm('Are you sure you want to close without applying the credit?', {
                    title: 'Close Without Applying',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Yes, Close',
                    cancelTitle: 'No, Stay',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                })
                .then(value => {
                    if (value) {
                        this.$bvModal.hide(this.modalName);
                    }
                })
                .catch(err => {
                    // An error occurred
                });
            } else {
                this.$bvModal.hide(this.modalName);
            }
        },

        focusInput() {
            this.$refs.setcreditinput.focus();
        }
    },

    computed: {
        isValidCreditAmount() {
            const amount = parseFloat(this.creditAmount);
            // Allow credit application if there's a balance due OR if there are row quantities (even with $0 balance)
            const hasRowQuantities = this.hasRowQuantities;
            return amount > 0 && amount <= this.totalAvailableCredit && (this.orderTotal > 0 || hasRowQuantities);
        },
        
        hasRowQuantities() {
            // Check if there are any rows with quantities > 0
            if (!this.rowQuantities || !Array.isArray(this.rowQuantities)) {
                return false;
            }
            return this.rowQuantities.some(row => 
                row.qty && parseFloat(row.qty) > 0
            );
        },

        usableBalanceDue() {
            // If there's an actual balance due, use that
            if (this.orderTotal > 0) {
                return this.orderTotal;
            }
            
            // If balance due is $0 but we have row quantities, calculate usable balance
            if (this.orderTotal === 0 && this.hasRowQuantities) {
                // Calculate total value based on row quantities
                let totalValue = 0;
                this.rowQuantities.forEach(row => {
                    const qty = row.qty;
                    const price = row.rate;
                    if (qty && parseFloat(qty) > 0 && price) {
                        totalValue += parseFloat(qty) * parseFloat(price);
                    }
                });
                return totalValue;
            }
            
            return this.orderTotal;
        }
    },

    mounted() {

    }
}
</script>
<style lang="scss">
#modal-orders-sales-orders-apply-credit {
    .modal-dialog {
        max-width: 90% !important;
    }

    .modal-content {
        .button-row {
            margin-top: 15px;
        }

        .text-danger {
            color: red;
        }

        .text-warning {
            color: orange;
        }

        .text-success {
            color: green;
        }

        .admin-only-input {
            background-color: #f8f9fa !important;
            color: #6c757d !important;
            cursor: not-allowed !important;
        }

        .credit-row {
            background-color: #007bff !important;
            color: white !important;
            padding: 10px;
            border-radius: 5px;
            margin-bottom: 10px;
        }

        .credit-row strong {
            color: white !important;
        }

        .credit-row .form-control {
            background-color: white !important;
            color: #333 !important;
        }

        .credit-row .btn-primary {
            background-color: #0056b3 !important;
            border-color: #0056b3 !important;
            color: white !important;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2) !important;
        }

        .credit-row .btn-primary:hover {
            background-color: #004085 !important;
            border-color: #004085 !important;
        }

        .overpayment-row {
            background-color: #ffc107 !important;
            color: #212529 !important;
            padding: 10px;
            border-radius: 5px;
            margin-bottom: 10px;
        }

        .overpayment-row strong {
            color: #212529 !important;
        }

        .overpayment-row .form-control {
            background-color: white !important;
            color: #333 !important;
        }

        .admin-only-text {
            color: #000 !important;
            font-weight: bold !important;
        }
    }
}
</style>
